import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("再設定リンクが無効です");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("再設定リンクが無効です");
      return;
    }
    if (pw.length < 6) {
      toast.error("パスワードは6文字以上で入力してください");
      return;
    }
    if (pw !== pw2) {
      toast.error("確認用パスワードが一致しません");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("パスワードを再設定しました");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-7 shadow-xl border border-slate-200">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center">
            <Lock className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 leading-tight">パスワード再設定</h1>
            <p className="text-xs text-slate-500 mt-0.5">新しいパスワードを設定してください</p>
          </div>
        </div>

        {!token ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
            <p className="text-sm text-slate-700 leading-relaxed">
              再設定リンクが無効、または期限切れです。<br />
              <Link to="/forgot-password" className="text-blue-600 font-bold underline">こちらから再度お試しください</Link>
            </p>
          </div>
        ) : done ? (
          <div className="text-center py-6" data-testid="reset-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="text-base font-bold text-slate-900">設定完了</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              新しいパスワードでログインできます。
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="mt-6 w-full bg-blue-600 hover:bg-blue-700"
              data-testid="reset-go-login"
            >
              ログイン画面へ
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div>
              <Label className="text-xs text-slate-600">新しいパスワード（6文字以上）</Label>
              <Input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mt-1 h-11 text-base"
                autoComplete="new-password"
                autoFocus
                data-testid="reset-pw"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">新しいパスワード（確認）</Label>
              <Input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="mt-1 h-11 text-base"
                autoComplete="new-password"
                data-testid="reset-pw2"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 h-11 text-sm font-semibold"
              data-testid="reset-submit"
            >
              {submitting ? "送信中…" : "パスワードを再設定する"}
            </Button>
            <p className="text-[11px] text-slate-400 text-center leading-relaxed">
              リンクの有効期限は発行から60分です。
            </p>
          </form>
        )}
      </Card>
    </div>
  );
}
